import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import {
  inputClass,
  labelClass,
  ghostButtonClass,
  primaryButtonClass,
} from "@/components/base/fieldStyles";
import type { Receivable } from "@/types";
import NumberInput from "@/components/base/NumberInput";
import { formatCOP, formatDate } from "@/utils/format";
import { receivablePaid, receivableBalance } from "@/utils/accounting";

interface PaymentModalProps {
  open: boolean;
  receivable: Receivable | null;
  onClose: () => void;
  onSubmit: (r: Receivable) => void;
}

// Registro de abonos de una cuenta por cobrar: abonos ILIMITADOS, cada uno con
// fecha y valor, y el historial completo siempre a la vista.
const PaymentModal = ({ open, receivable, onClose, onSubmit }: PaymentModalProps) => {
  const [amount, setAmount] = useState(0);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setAmount(0);
    setDate(new Date().toISOString().slice(0, 10));
    setError("");
  }, [open, receivable]);

  if (!receivable) {
    return (
      <Modal open={open} onClose={onClose} title="Registrar abono" icon="ri-hand-coin-line">
        <p className="text-sm text-foreground-600">Selecciona una cuenta.</p>
      </Modal>
    );
  }

  const paid = receivablePaid(receivable);
  const balance = receivableBalance(receivable);
  const settled = balance <= 0;

  const submit = () => {
    const value = Number(amount) || 0;
    if (value <= 0) {
      setError("El abono debe ser mayor a cero.");
      return;
    }
    if (value > balance) {
      setError(`El abono no puede superar el saldo pendiente de ${formatCOP(balance)}.`);
      return;
    }
    onSubmit({
      ...receivable,
      payments: [...receivable.payments, { amount: value, date: new Date(date).toISOString() }],
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Registrar abono · ${receivable.clientName}`}
      icon="ri-hand-coin-line"
      footer={
        <>
          <button onClick={onClose} className={ghostButtonClass}>
            Cancelar
          </button>
          <button onClick={submit} className={primaryButtonClass} disabled={settled}>
            <i className="ri-check-double-line" /> Aplicar abono
          </button>
        </>
      }
    >
      <div className="rounded-md bg-background-200/40 border border-background-300/40 px-4 py-3 mb-4">
        <div className="flex items-center justify-between text-xs text-foreground-600">
          <span>Valor total</span>
          <span className="font-semibold text-foreground-950">{formatCOP(receivable.initialValue)}</span>
        </div>
        <div className="flex items-center justify-between text-xs text-foreground-600 mt-1">
          <span>Abonado</span>
          <span className="font-semibold text-accent-200">{formatCOP(paid)}</span>
        </div>
        <div className="flex items-center justify-between text-xs text-foreground-600 mt-1">
          <span>Saldo pendiente</span>
          <span className="font-bold text-foreground-950">{formatCOP(balance)}</span>
        </div>
      </div>

      {settled ? (
        <div className="rounded-md bg-accent-500/15 border border-accent-500/30 px-4 py-3 mb-4 flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-accent-200" />
          <p className="text-xs text-foreground-800">Esta cuenta ya está saldada. No hay saldo por abonar.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Monto del abono (COP)</label>
            <NumberInput min={0} value={amount} onChange={setAmount} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Fecha del abono</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
      )}

      {error && (
        <p className="text-[11px] text-primary-200 mt-3 flex items-center gap-1.5">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}

      <div className="mt-5">
        <p className="text-[11px] uppercase tracking-wider text-foreground-500 mb-2">
          Historial de abonos ({receivable.payments.length})
        </p>
        {receivable.payments.length === 0 ? (
          <p className="text-xs text-foreground-500 rounded-md bg-background-200/40 border border-background-300/40 px-3 py-3">
            Todavía no hay abonos registrados en esta cuenta.
          </p>
        ) : (
          <ul className="max-h-52 overflow-y-auto rounded-md border border-background-300/40 divide-y divide-background-300/30">
            {receivable.payments.map((p, idx) => (
              <li
                key={`${idx}-${p.date}-${p.amount}`}
                className="flex items-center justify-between gap-3 px-3 py-2.5"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="w-6 h-6 shrink-0 rounded-full bg-accent-500/20 text-accent-200 flex items-center justify-center text-[10px] font-bold">
                    {idx + 1}
                  </span>
                  <span className="text-xs text-foreground-600 truncate">
                    {p.date ? formatDate(p.date) : "Sin fecha"}
                  </span>
                </span>
                <span className="text-sm font-semibold text-accent-200 whitespace-nowrap">
                  {formatCOP(p.amount)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
};

export default PaymentModal;